import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import LoadingButton from "../LoadingButton/LoadingButton";
import "./ReportsContent.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ReportsContent = () => {
  const [activeTab, setActiveTab] = useState("monthly");
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const tableRef = useRef(null);

  // Kullanıcı listesini getir
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("user_id, name, surname")
          .eq("is_active", true)
          .order("name");

        if (!error) {
          setUsers(data || []);
        }
      } catch (error) {
        console.error("Kullanıcılar çekilemedi:", error);
      }
    };

    fetchUsers();
  }, []);

  // Sekme değiştiğinde varsayılan tarihleri ayarla
  useEffect(() => {
    setDefaultDateRange();
  }, [activeTab]);

  // Varsayılan tarih aralığını ayarla
  const setDefaultDateRange = () => {
    const today = new Date();

    switch (activeTab) {
      case "daily":
        // Günlük: Bugün
        const todayStr = today.toISOString().split("T")[0];
        setStartDate(todayStr);
        setEndDate(todayStr);
        break;

      case "weekly":
        // Haftalık: Bu haftanın Pazartesi'sinden Pazar'a
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Pazar günü ise bir önceki pazartesi
        const monday = new Date(today.setDate(diff));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        setStartDate(monday.toISOString().split("T")[0]);
        setEndDate(sunday.toISOString().split("T")[0]);
        break;

      case "monthly":
        // Aylık: Bu ayın ilk gününden son güne
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        setStartDate(firstDay.toISOString().split("T")[0]);
        setEndDate(lastDay.toISOString().split("T")[0]);
        break;

      case "overtime":
        // Fazla Mesai: Bu ayın ilk gününden bugüne
        const overtimeFirstDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );

        setStartDate(overtimeFirstDay.toISOString().split("T")[0]);
        setEndDate(today.toISOString().split("T")[0]);
        break;

      default:
        setStartDate("");
        setEndDate("");
    }
  };

  // Raporları getir
  const fetchReports = async (isFiltering = false) => {
    if (isFiltering) {
      setApplyingFilters(true);
    } else {
      setLoading(true);
    }

    try {
      let data = [];

      if (startDate && endDate) {
        let query = supabase
          .from("daily_attendance")
          .select(
            `
            *,
            users (name, surname)
          `
          )
          .gte("work_date", startDate)
          .lte("work_date", endDate)
          .order("work_date", { ascending: false });

        const { data: reportData, error } = await query;

        if (!error) {
          data = reportData || [];

          // Fazla mesai raporu için ek filtreleme
          if (activeTab === "overtime") {
            data = data.filter((record) => {
              if (record.first_in_time && record.last_out_time) {
                const checkIn = new Date(record.first_in_time);
                const checkOut = new Date(record.last_out_time);
                const hoursWorked = (checkOut - checkIn) / (1000 * 60 * 60);
                return hoursWorked > 8;
              }
              return false;
            });
          }
        }
      }

      const processedData = data.map((record) => {
        if (!record.users && record.profiles) {
          const [name, ...surnameParts] = record.profiles.full_name.split(" ");
          record.users = {
            name: name,
            surname: surnameParts.join(" "),
          };
        }
        return record;
      });

      setReports(processedData);
      setFilteredReports(processedData);
    } catch (error) {
      console.error("Raporlar çekilemedi:", error);
    } finally {
      if (isFiltering) {
        setApplyingFilters(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Filtreleri uygula
  useEffect(() => {
    let result = [...reports];

    // Kullanıcı filtresi
    if (selectedUser !== "all") {
      result = result.filter(
        (report) => report.user_id === parseInt(selectedUser)
      );
    }

    setFilteredReports(result);
  }, [reports, selectedUser]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const formatWorkHours = (hours) => {
    if (hours === null || hours === undefined || hours === "") return "-";
    const num = parseFloat(hours);
    if (isNaN(num)) return "-";
    const sign = num < 0 ? "-" : "";
    const absHours = Math.abs(num);
    const h = Math.floor(absHours);
    const m = Math.round((absHours - h) * 60);
    if (m === 60) {
      return `${sign}${h + 1}:00`;
    }
    return `${sign}${h}:${m.toString().padStart(2, "0")}`;
  };

  // PDF İndirme Fonksiyonu
  const downloadPDF = async () => {
    if (!tableRef.current) return;

    setDownloadingPDF(true);
    try {
      const canvas = await html2canvas(tableRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      let reportName = "";
      switch (activeTab) {
        case "daily":
          reportName = "Günlük Rapor";
          break;
        case "weekly":
          reportName = "Haftalık Rapor";
          break;
        case "monthly":
          reportName = "Aylık Rapor";
          break;
        case "overtime":
          reportName = "Fazla Mesai Raporu";
          break;
        default:
          reportName = "Rapor";
      }

      pdf.save(`${reportName}.pdf`);
    } catch (error) {
      console.error("PDF oluşturma hatası:", error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Filtreleri uygula butonu
  const applyFilters = () => {
    fetchReports(true);
  };

  // Filtreleri sıfırla
  const resetFilters = () => {
    setSelectedUser("all");
    setDefaultDateRange();
    // Tarihleri sıfırladıktan sonra verileri yeniden çek
    fetchReports(true);
  };

  // Tarih aralığını değiştir (önceki/sonraki)
  const changeDateRange = (direction) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    switch (activeTab) {
      case "daily":
        // Günlük: Bir gün ileri/geri
        const newDailyDate = new Date(start);
        newDailyDate.setDate(start.getDate() + direction);
        const dailyStr = newDailyDate.toISOString().split("T")[0];
        setStartDate(dailyStr);
        setEndDate(dailyStr);
        break;

      case "weekly":
        // Haftalık: Bir hafta ileri/geri
        const newWeekStart = new Date(start);
        const newWeekEnd = new Date(end);
        newWeekStart.setDate(start.getDate() + 7 * direction);
        newWeekEnd.setDate(end.getDate() + 7 * direction);
        setStartDate(newWeekStart.toISOString().split("T")[0]);
        setEndDate(newWeekEnd.toISOString().split("T")[0]);
        break;

      case "monthly":
        // Aylık: Bir ay ileri/geri
        const newMonthStart = new Date(start);
        const newMonthEnd = new Date(end);
        newMonthStart.setMonth(start.getMonth() + direction);
        newMonthEnd.setMonth(end.getMonth() + direction);
        setStartDate(newMonthStart.toISOString().split("T")[0]);
        setEndDate(newMonthEnd.toISOString().split("T")[0]);
        break;

      case "overtime":
        // Fazla Mesai: Bir ay ileri/geri
        const newOvertimeStart = new Date(start);
        const newOvertimeEnd = new Date(end);
        newOvertimeStart.setMonth(start.getMonth() + direction);
        newOvertimeEnd.setDate(new Date().getDate()); // Bitiş tarihi her zaman bugün
        setStartDate(newOvertimeStart.toISOString().split("T")[0]);
        setEndDate(newOvertimeEnd.toISOString().split("T")[0]);
        break;
    }
  };

  useEffect(() => {
    fetchReports();
  }, [activeTab, startDate, endDate]);

  if (loading) {
    return (
      <div className="reports-content">
        <div className="reports-list">
          <div className="list-header">
            <h2>Raporlar</h2>
          </div>
          <div className="loading-container">
            <div className="orbit-loader-wrapper">
              <OrbitLoaderComponent size="medium" showPath={true} />
            </div>
            <p className="loading-text">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-content">
      <div className="reports-list">
        <div className="list-header">
          <h2>Raporlar</h2>
          <div className="tab-buttons">
            <LoadingButton
              className={`tab-btn ${activeTab === "daily" ? "active" : ""}`}
              onClick={() => handleTabChange("daily")}
              isLoading={false}
            >
              Günlük
            </LoadingButton>
            <LoadingButton
              className={`tab-btn ${activeTab === "weekly" ? "active" : ""}`}
              onClick={() => handleTabChange("weekly")}
              isLoading={false}
            >
              Haftalık
            </LoadingButton>
            <LoadingButton
              className={`tab-btn ${activeTab === "monthly" ? "active" : ""}`}
              onClick={() => handleTabChange("monthly")}
              isLoading={false}
            >
              Aylık
            </LoadingButton>
            <LoadingButton
              className={`tab-btn ${activeTab === "overtime" ? "active" : ""}`}
              onClick={() => handleTabChange("overtime")}
              isLoading={false}
            >
              Fazla Mesai
            </LoadingButton>
          </div>
        </div>

        {/* Filtreleme Alanı */}
        <div className="filter-section">
          <div className="filter-group">
            <div className="filter-item">
              <label htmlFor="user-select">Personel:</label>
              <select
                id="user-select"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="filter-select"
              >
                <option value="all">Tüm Personel</option>
                {users.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.name} {user.surname}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-item date-range">
              <label>Tarih Aralığı:</label>
              <div className="date-range-container">
                <div className="date-nav">
                  <button
                    className="date-nav-btn"
                    onClick={() => changeDateRange(-1)}
                    title="Önceki"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>

                  <div className="date-inputs">
                    <div className="date-input-wrapper">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="filter-input"
                      />
                      <span className="date-separator">-</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="filter-input"
                      />
                    </div>
                  </div>
                  <button
                    className="date-nav-btn"
                    onClick={() => changeDateRange(1)}
                    title="Sonraki"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="filter-actions">
              <button
                onClick={applyFilters}
                disabled={applyingFilters || (!startDate && !endDate)}
                className="filter-apply-btn"
              >
                {applyingFilters ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Uygulanıyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-filter"></i> Filtrele
                  </>
                )}
              </button>

              <button onClick={resetFilters} className="filter-reset-btn">
                <i className="fas fa-redo"></i> Sıfırla
              </button>

              <button
                onClick={downloadPDF}
                disabled={downloadingPDF || filteredReports.length === 0}
                className="pdf-download-btn"
              >
                {downloadingPDF ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-pdf"></i> PDF İndir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="reports-table" ref={tableRef}>
            <thead>
              <tr>
                <th>Personel</th>
                <th>Tarih</th>
                {activeTab === "daily" && (
                  <>
                    <th>Giriş</th>
                    <th>Çıkış</th>
                    <th>Çalışma Süresi</th>
                  </>
                )}
                {activeTab === "weekly" && (
                  <>
                    <th>Giriş</th>
                    <th>Çıkış</th>
                    <th>Çalışma Süresi</th>
                  </>
                )}
                {activeTab === "monthly" && (
                  <>
                    <th>Giriş</th>
                    <th>Çıkış</th>
                    <th>Çalışma Süresi</th>
                  </>
                )}
                {activeTab === "overtime" && (
                  <>
                    <th>Çalışma Süresi</th>
                    <th>Fazla Mesai</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredReports.length > 0 ? (
                filteredReports.map((report, index) => {
                  let hoursWorked = 0;
                  let overtimeHours = 0;
                  if (report.first_in_time && report.last_out_time) {
                    const checkIn = new Date(report.first_in_time);
                    const checkOut = new Date(report.last_out_time);
                    hoursWorked = (checkOut - checkIn) / (1000 * 60 * 60);
                    overtimeHours = hoursWorked > 8 ? hoursWorked - 8 : 0;
                  }
                  return (
                    <tr key={index}>
                      <td>
                        <strong>
                          {report.users?.name} {report.users?.surname}
                        </strong>
                      </td>
                      <td>
                        {new Date(report.work_date).toLocaleDateString("tr-TR")}
                      </td>
                      {(activeTab === "daily" ||
                        activeTab === "weekly" ||
                        activeTab === "monthly") && (
                        <>
                          <td>
                            {report.first_in_time
                              ? new Date(
                                  report.first_in_time
                                ).toLocaleTimeString("tr-TR")
                              : "-"}
                          </td>
                          <td>
                            {report.last_out_time
                              ? new Date(
                                  report.last_out_time
                                ).toLocaleTimeString("tr-TR")
                              : "-"}
                          </td>
                          <td>
                            <span className="work-hours">
                              {formatWorkHours(hoursWorked)}
                            </span>
                          </td>
                        </>
                      )}
                      {activeTab === "overtime" && (
                        <>
                          <td>
                            <span className="work-hours">
                              {formatWorkHours(hoursWorked)}
                            </span>
                          </td>
                          <td>
                            <span className="overtime-hours">
                              {formatWorkHours(overtimeHours)}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={activeTab === "overtime" ? 4 : 5}
                    className="no-data"
                  >
                    📭 Veri bulunamadı
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportsContent;
